"use client";

import { useQuery } from "@tanstack/react-query";
import { Effect } from "effect";
import { RpcClient } from "@effect/rpc";
import { DEFAULT_PLATFORM_SCOPE } from "@/domain/scope/platform-scope";
import type { SpotlightScope, SpotlightView } from "@/contexts/feed/domain/spotlight/spotlight-settings";
import { feedSpotlightEq, FeedSpotlightRpc } from "./api/feed-spotlight-client";
import { spotlightKeys } from "./keys";
const EMPTY: ReadonlyArray<SpotlightView> = [];

/** A tab left open re-asks for its queue at most once an hour, on focus. */
export const QUEUE_STALE_MS = 60 * 60 * 1000;

/**
 * The spotlights waiting for this broker on this surface, newest first, each
 * with its button already resolved by the server.
 *
 * Fetched once on load, and again on window focus at most once an hour, so a
 * tab left open picks up a new spotlight within the hour rather than only on
 * reload. A popup is never worth a failure, so an error resolves to an empty
 * queue.
 */
export function useSpotlightQueue(surface: SpotlightScope | null): ReadonlyArray<SpotlightView> {
  const {
    data
  } = useQuery({
    ...feedSpotlightEq.queryOptions({
      queryKey: spotlightKeys.queue(surface ?? DEFAULT_PLATFORM_SCOPE),
      queryFn: () => Effect.gen(function* () {
        const rpc = yield* RpcClient.make(FeedSpotlightRpc);
        return yield* rpc.getSpotlightQueue({
          surface: surface ?? DEFAULT_PLATFORM_SCOPE
        });
      }).pipe(Effect.scoped),
      staleTime: QUEUE_STALE_MS
    }),
    enabled: surface !== null,
    // Refetches on focus only once the hour is up (TanStack's focus refetch
    // skips fresh data).
    refetchOnWindowFocus: true,
    retry: false
  });
  return data?.items ?? EMPTY;
}